import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NameFinderComponent } from './name-finder/name-finder.component';
import {
  NamesRegisterItem,
  NamesRegisterResponse,
  NamesRegisterService,
} from 'src/app/services/names-register-service';
import { ALPHABET, COUNTRY_NAMES, HERO_SET } from './register-fixture';
import { CharCell, flagEmoji, gbp, plateChars, toChars } from './plate-chars';

/* =============================================================================
   PlateX — Names · The Perfect Register.
   Task 3: shell and static structure (approved).
   Task 4: the live Register — wired to `process=names-register`, which serves
   the prepared, versioned artifact (names build pack 02-data-contract §8/§11).

   Rules enforced here:
     · every total rendered from the API response; nothing hard-coded (G1)
     · one registration renders once, carrying all its names and languages
     · plate first, name beneath — the order is an acceptance criterion
     · era captions come from the server, never derived client-side
     · availability `unknown` is a first-class state (never mis-shown as held)
     · incremental paging appends items; unchanged DOM nodes are preserved
       via trackBy (test D3)
   ========================================================================== */

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const PAGE_SIZE = 60;

interface HeroVM {
  name: string;
  note: string;
  chars: CharCell[];
}

interface ItemVM extends NamesRegisterItem {
  chars: CharCell[];
  eraLabel: string;
}

@Component({
  selector: 'pla-names-register',
  templateUrl: './names-register.component.html',
  styleUrls: ['./names-register.component.scss'],
})
export class NamesRegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  public letters = LETTERS;
  public alphabet = ALPHABET;

  /* ---- hero reveal (approved set — every entry engine-validated) ---- */
  public hero: HeroVM[] = HERO_SET.map((h) => ({ name: h.name, note: h.note, chars: toChars(h.display, h.subIdx) }));
  public heroI = 0;
  public heroNameOn = false;
  public reducedMotion = false;
  private heroTimer: any = null;
  private heroRevealTimer: any = null;

  /* ---- live register state ---- */
  public items: ItemVM[] = [];
  public loading = true;          // initial load: skeleton grid
  public refreshing = false;      // filter change: grid stays mounted (D3), dimmed
  public loadingMore = false;     // subsequent pages
  public loadFailed = false;
  public total: number | null = null;            // current filter count
  public totalUnfiltered: number | null = null;  // the Register count (live)
  public page = 1;
  public availabilityLive = true;                // false => degraded join upstream
  public generatedAt: string | null = null;
  public datasetVersion: number | null = null;

  public origin: 'all' | string = 'all';
  public avail: 'all' | 'new-issue' | 'resale' | 'previously-sold' | 'held' = 'all';
  public letter: 'all' | string = 'all';
  public originOptions: { code: string; label: string; count: number }[] = [];
  public presentLetters = new Set<string>();

  /* ---- live evidence highlights (real language tags; §9 notes) ---- */
  public travelNames: { name: string; languages: string[] }[] = [];
  public belongNames: { name: string; language: string }[] = [];
  public recordSale: NamesRegisterResponse['recordSale'] = null;
  /** How rare a current-style plate is on the Register: "1 in N". Live, not fixed. */
  public currentShare: number | null = null;

  private requestSeq = 0;

  /** The Finder, so a ?name= deep link (homepage Names tab) can run it. */
  @ViewChild(NameFinderComponent) private finder?: NameFinderComponent;

  constructor(
    private el: ElementRef<HTMLElement>,
    private router: Router,
    private route: ActivatedRoute,
    private registerService: NamesRegisterService
  ) {}

  ngOnInit(): void {
    this.reducedMotion =
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.showHero(0);
    if (!this.reducedMotion) {
      this.heroTimer = setInterval(() => this.showHero((this.heroI + 1) % this.hero.length), 5200);
    }
    this.fetch(true);
    // Hero + Out-of-Reach validation runs at build time (plato-search
    // test_names_register.py), not per page load, so a visitor's first
    // interaction is not preceded by 8 resolve calls.
  }

  ngAfterViewInit(): void {
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    const name = (this.route.snapshot.queryParamMap.get('name') || '').trim();
    if (name && this.finder) {
      setTimeout(() => {
        this.finder?.run(name);
        this.el.nativeElement.querySelector('pla-name-finder')?.scrollIntoView({ block: 'start' });
      }, 300);
    }
    window.addEventListener('resize', this.measureShellBound, { passive: true });
    const tb = this.el.nativeElement.querySelector('.toolbar');
    if (tb && typeof ResizeObserver !== 'undefined') {
      this.toolbarObserver = new ResizeObserver(() => this.measureShellSettled());
      this.toolbarObserver.observe(tb);
    }
  }

  private toolbarObserver: ResizeObserver | null = null;

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.measureShellBound);
    if (this.toolbarObserver) this.toolbarObserver.disconnect();
    if (this.heroTimer) clearInterval(this.heroTimer);
    if (this.heroRevealTimer) clearTimeout(this.heroRevealTimer);
  }

  /* =============== live register =============== */
  public fetch(reset: boolean): void {
    const seq = ++this.requestSeq;
    if (reset) {
      this.page = 1;
      this.loadFailed = false;
      // first ever load shows the skeleton; later filter changes refresh the
      // mounted grid in place so unchanged items keep their DOM nodes (D3)
      if (this.items.length === 0) this.loading = true;
      else this.refreshing = true;
    } else {
      this.loadingMore = true;
    }
    this.registerService
      .getRegister({ language: this.origin, availability: this.avail, letter: this.letter, page: this.page, size: PAGE_SIZE })
      .subscribe(
        (res: NamesRegisterResponse) => {
          if (seq !== this.requestSeq) return; // stale response — a newer query is in flight
          this.ingest(res, reset);
        },
        () => {
          if (seq !== this.requestSeq) return;
          this.loading = false;
          this.refreshing = false;
          this.loadingMore = false;
          this.loadFailed = true;
        }
      );
  }

  private ingest(res: NamesRegisterResponse, reset: boolean): void {
    const mapped = res.items.map((it) => ({
      ...it,
      chars: plateChars(it.display),
      eraLabel: it.era ? it.era.charAt(0).toUpperCase() + it.era.slice(1) : '',
    }));
    this.items = reset ? mapped : this.items.concat(mapped);
    this.total = res.total;
    this.totalUnfiltered = res.totalUnfiltered;
    this.availabilityLive = res.metadata.availability.joined;
    this.generatedAt = res.metadata.generatedAt;
    this.datasetVersion = res.metadata.datasetVersion;
    this.presentLetters = new Set(Object.keys(res.facets.letters).filter((l) => res.facets.letters[l] > 0));
    this.originOptions = Object.keys(res.facets.languages)
      .map((code) => ({ code, label: COUNTRY_NAMES[code] || code.toUpperCase(), count: res.facets.languages[code] }))
      .sort((a, b) => b.count - a.count);
    if (res.highlights) {
      this.travelNames = res.highlights.travel || [];
      this.belongNames = res.highlights.belong || [];
    }
    if (res.recordSale) this.recordSale = res.recordSale;
    // "The modern plate is the hostile one" is rendered from the era split, so
    // the figure moves with the data instead of being asserted.
    const era = res.eraCounts;
    if (era && era['current'] > 0 && res.totalUnfiltered) {
      this.currentShare = Math.round(res.totalUnfiltered / era['current']);
    }
    this.loading = false;
    this.refreshing = false;
    this.loadingMore = false;
  }

  public get hasMore(): boolean {
    return this.total !== null && this.items.length < this.total;
  }

  public loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) return;
    this.page += 1;
    this.fetch(false);
  }

  public trackByReg(_i: number, item: ItemVM): string {
    return item.registration;
  }

  public stateLine(d: ItemVM): string {
    const price = gbp(d.pricePence);
    if (d.state === 'new-issue') return price ? `New issue · ${price}` : 'New issue · DVLA';
    if (d.state === 'resale') return price ? `For sale · from ${price}` : 'For sale';
    if (d.state === 'held') return 'Held';
    if (d.state === 'unknown') return 'Availability unknown';
    if (d.lastSale && d.lastSale.verified) return `Last sold ${gbp(d.lastSale.pricePence)}`;
    return 'Previously sold';
  }

  public flag(code: string): string {
    return flagEmoji(code);
  }

  public chars(display: string): CharCell[] {
    return plateChars(display);
  }

  /** Route to the normal PlateX search for a name (Out of Reach alternatives). */
  public searchFor(name: string): void {
    this.router.navigate(['/search-results', name]);
  }

  public isMutedState(d: ItemVM): boolean {
    return d.state === 'held' || d.state === 'previously-sold' || d.state === 'unknown';
  }

  /* =============== toolbar handlers =============== */
  public setAvail(a: 'all' | 'new-issue' | 'resale' | 'previously-sold' | 'held'): void {
    if (this.avail === a) return;
    this.avail = a;
    this.fetch(true);
  }

  public setOrigin(o: string): void {
    if (this.origin === o) return;
    this.origin = o;
    this.fetch(true);
  }

  public setLetter(l: 'all' | string, ev?: Event): void {
    if (ev) ev.preventDefault();
    if (this.letter === l) return;
    this.letter = l;
    this.fetch(true);
    if (l !== 'all') {
      const target = this.el.nativeElement.querySelector('#register') as HTMLElement | null;
      if (target) target.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  public countryLabel(code: string): string {
    return COUNTRY_NAMES[code] || code.toUpperCase();
  }

  /* =============== shell measurement (see Task 3 report) =============== */
  private measureShellBound = (): void => this.measureShellSettled();

  private measureShellSettled(): void {
    requestAnimationFrame(() => this.measureShell());
    setTimeout(() => this.measureShell(), 250);
  }

  private measureShell(): void {
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
    const tb = this.el.nativeElement.querySelector('.toolbar') as HTMLElement | null;
    if (tb && tb.offsetHeight > 0) {
      this.el.nativeElement.style.setProperty('--tbH', `${Math.round(tb.offsetHeight)}px`);
    }
  }

  /* =============== hero =============== */
  public showHero(i: number, userDriven = false): void {
    if (userDriven && this.heroTimer) {
      clearInterval(this.heroTimer);
      this.heroTimer = null;
    }
    this.heroI = i;
    if (this.heroRevealTimer) clearTimeout(this.heroRevealTimer);
    if (this.reducedMotion || userDriven) {
      this.heroNameOn = true;
      return;
    }
    this.heroNameOn = false;
    this.heroRevealTimer = setTimeout(() => (this.heroNameOn = true), 900);
  }

  /* =============== navigation =============== */
  public plateLink(plain: string): string[] {
    return ['/plate', plain.replace(/\s+/g, '')];
  }

  public jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (target) target.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }
}
